class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('shopify-section-cart-drawer');
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.innerDrawer = document.getElementById('cart-drawer');
    this.isUpdating = false;

    if (!this.drawer) return;

    this.bindEvents();
    this.interceptAddToCart();
    this.restoreSavedCart();
  }

  restoreSavedCart() {
    const savedCartStr = localStorage.getItem('zuboc_saved_cart');
    if (savedCartStr) {
      try {
        const savedCart = JSON.parse(savedCartStr);
        // Only restore if saved within the last 24 hours
        if (Date.now() - savedCart.timestamp < 1000 * 60 * 60 * 24) {
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: savedCart.items })
          }).then(res => res.json()).then(() => {
            this.updateDrawer();
            this.updateMainCart();
          }).catch(console.error);
        }
      } catch (e) {
        console.error('Error restoring cart', e);
      }
      localStorage.removeItem('zuboc_saved_cart');
    }
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      const cartLink = e.target.closest('a[href="/cart"]');
      if (cartLink && !cartLink.closest('#cart-drawer') && !cartLink.closest('#cart-form')) {
        e.preventDefault();
        this.open();
      }
    });

    window.closeCartDrawer = this.close.bind(this);
    window.updateCartItem = this.updateItem.bind(this);
    window.addCartItem = this.addItem.bind(this);

    // Buy It Now — collect full form data then redirect to checkout
    document.addEventListener('click', async (e) => {
      const buyNowBtn = e.target.closest('[data-buy-now]');
      if (!buyNowBtn) return;
      await this.buyNow(buyNowBtn);
    });
  }

  // ─── CORE FIX: Collect ALL form data including outside-form elements ───────
  // Safari and some browsers do NOT include elements associated via the
  // form="id" attribute when outside the <form> tag in new FormData(form).
  // This explicitly collects and appends them to ensure properties are saved.
  collectFormData(form) {
    const formData = new FormData(form);
    const formId = form.getAttribute('id');

    if (!formId) return formData;

    // Find all elements associated with this form via the form="id" attribute
    // that are NOT physically inside the form element
    const associated = document.querySelectorAll('[form="' + formId + '"]');
    associated.forEach((field) => {
      // Skip if the browser already included it (it's inside the form)
      if (form.contains(field)) return;
      // Skip disabled or unnamed fields
      if (field.disabled || !field.name) return;

      if (field.type === 'file') {
        // File inputs — must append each file individually
        if (field.files && field.files.length > 0) {
          formData.delete(field.name); // remove any empty placeholder
          for (let i = 0; i < field.files.length; i++) {
            formData.append(field.name, field.files[i], field.files[i].name);
          }
        }
      } else if (field.type === 'checkbox' || field.type === 'radio') {
        if (field.checked) {
          formData.set(field.name, field.value);
        }
      } else if (field.tagName === 'SELECT') {
        Array.from(field.selectedOptions).forEach((opt) => {
          formData.set(field.name, opt.value);
        });
      } else {
        // text, textarea, number, hidden, etc.
        formData.set(field.name, field.value);
      }
    });

    return formData;
  }

  // ─── Validate all required fields (inside + outside the form) ─────────────
  validateForm(form) {
    const formId = form.getAttribute('id');

    const insideRequired = Array.from(form.querySelectorAll('[required]'));
    const outsideRequired = formId
      ? Array.from(document.querySelectorAll('[required][form="' + formId + '"]')).filter(
          (f) => !form.contains(f)
        )
      : [];

    for (const field of [...insideRequired, ...outsideRequired]) {
      if (field.type === 'file') {
        if (!field.files || field.files.length === 0) {
          // Scroll the field into view and show browser validation UI
          field.scrollIntoView({ behavior: 'smooth', block: 'center' });
          field.reportValidity();
          return false;
        }
      } else {
        if (!field.value || field.value.trim() === '') {
          field.scrollIntoView({ behavior: 'smooth', block: 'center' });
          field.reportValidity();
          return false;
        }
      }
    }

    return true;
  }

  // ─── Redirect to checkout reliably ────────────────────────────────────────
  // window.location.href = '/checkout' can be blocked in Shopify preview.
  // Using a real native form POST is the most reliable approach.
  redirectToCheckout() {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/checkout';
    document.body.appendChild(form);
    form.submit();
  }

  open() {
    const overlay = document.getElementById('cart-drawer-overlay');
    const drawer = document.getElementById('cart-drawer');
    if (overlay && drawer) {
      overlay.classList.remove('invisible', 'opacity-0');
      overlay.classList.add('opacity-100');
      drawer.classList.remove('translate-x-full');
      document.body.style.overflow = 'hidden';
      if (window.lenis) window.lenis.stop();
    }
  }

  close() {
    const overlay = document.getElementById('cart-drawer-overlay');
    const drawer = document.getElementById('cart-drawer');
    if (overlay && drawer) {
      overlay.classList.remove('opacity-100');
      overlay.classList.add('opacity-0');
      drawer.classList.add('translate-x-full');
      setTimeout(() => {
        overlay.classList.add('invisible');
        document.body.style.overflow = '';
        if (window.lenis) window.lenis.start();
      }, 300);
    }
  }

  setLoading(isLoading) {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    if (isLoading) {
      drawer.classList.add('pointer-events-none');
    } else {
      drawer.classList.remove('pointer-events-none');
    }
  }

  async updateDrawer() {
    try {
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newDrawer = doc.getElementById('cart-drawer');
      const currentDrawer = document.getElementById('cart-drawer');

      if (newDrawer && currentDrawer) {
        currentDrawer.replaceChildren(...newDrawer.childNodes);

        // Update header cart count badge using document, since the nodes were just moved to the live DOM
        const drawerCountVal = document.getElementById('cart-drawer-count-val');
        const headerBadge = document.getElementById('header-cart-count');
        if (drawerCountVal && headerBadge) {
          headerBadge.textContent = drawerCountVal.textContent;
        }

        // Re-initialize Swiper if needed
        if (typeof window.initCartUpsellSwiper === 'function') {
          window.initCartUpsellSwiper();
        }
      }
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    }
  }

  async updateMainCart() {
    const mainCartContainer = document.getElementById('main-cart-container');
    if (!mainCartContainer) return;

    try {
      const response = await fetch(window.location.pathname + '?section_id=main-cart');
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContainer = doc.getElementById('main-cart-container');

      if (newContainer) {
        mainCartContainer.replaceChildren(...newContainer.childNodes);
      }
    } catch (error) {
      console.error('Error updating main cart:', error);
    }
  }

  async evaluateFreeGift(cartData = null) {
    if (!window.cartDrawerSettings || !window.cartDrawerSettings.enableFreeGift) return false;

    const settings = window.cartDrawerSettings;
    const variantId = settings.freeGiftVariantId;
    if (!variantId) return false;

    try {
      let cart = cartData;
      if (!cart) {
        const cartResponse = await fetch('/cart.js');
        cart = await cartResponse.json();
      }

      let subtotal = cart.total_price;
      let freeGiftInCart = false;
      let freeGiftKey = null;

      cart.items.forEach((item) => {
        if (item.variant_id === variantId || item.product_id === settings.freeGiftProductId) {
          freeGiftInCart = true;
          freeGiftKey = item.key;
          subtotal -= item.final_line_price;
        }
      });

      if (subtotal >= settings.freeGiftThreshold && !freeGiftInCart) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        return true;
      } else if (subtotal < settings.freeGiftThreshold && freeGiftInCart) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: freeGiftKey, quantity: 0 }),
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error evaluating free gift:', error);
      return false;
    }
  }

  async updateItem(btn, key, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.setLoading(true);

    const spinnerHtml = `<svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`;
    const parseHTML = (html) => Array.from(new DOMParser().parseFromString(html, 'text/html').body.childNodes);

    let originalBtnNodes = [];
    if (btn && btn.classList) {
      originalBtnNodes = Array.from(btn.childNodes);
      btn.replaceChildren(...parseHTML(spinnerHtml));
      btn.classList.add('opacity-50', 'pointer-events-none');
    }

    const checkoutBtn = document.getElementById('cart-drawer-checkout-btn');
    let originalCheckoutNodes = [];
    if (checkoutBtn) {
      originalCheckoutNodes = Array.from(checkoutBtn.childNodes);
      checkoutBtn.replaceChildren(...parseHTML(`<span class="flex items-center justify-center gap-2">${spinnerHtml} Checkout</span>`));
      checkoutBtn.classList.add('opacity-70', 'pointer-events-none');
    }

    const mainCheckoutBtn = document.getElementById('main-cart-checkout-btn');
    let originalMainCheckoutNodes = [];
    if (mainCheckoutBtn) {
      originalMainCheckoutNodes = Array.from(mainCheckoutBtn.childNodes);
      mainCheckoutBtn.replaceChildren(...parseHTML(`<span class="flex items-center justify-center gap-2">${spinnerHtml} Check Out</span>`));
      mainCheckoutBtn.classList.add('opacity-70', 'pointer-events-none');
    }

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const cartData = await res.json();

      await this.evaluateFreeGift(cartData);
      await this.updateDrawer();
      await this.updateMainCart();
    } catch (error) {
      console.error('Error updating item:', error);
    } finally {
      this.isUpdating = false;
      this.setLoading(false);
      if (btn && btn.classList && btn.isConnected) {
        btn.replaceChildren(...originalBtnNodes);
        btn.classList.remove('opacity-50', 'pointer-events-none');
      }
      if (checkoutBtn && checkoutBtn.isConnected) {
        checkoutBtn.replaceChildren(...originalCheckoutNodes);
        checkoutBtn.classList.remove('opacity-70', 'pointer-events-none');
      }
      if (mainCheckoutBtn && mainCheckoutBtn.isConnected) {
        mainCheckoutBtn.replaceChildren(...originalMainCheckoutNodes);
        mainCheckoutBtn.classList.remove('opacity-70', 'pointer-events-none');
      }
    }
  }

  async buyNow(btn) {
    const spinnerHtml = `<svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`;
    const parseHTML = (html) => Array.from(new DOMParser().parseFromString(html, 'text/html').body.childNodes);
    const originalNodes = Array.from(btn.childNodes);

    const resetBtn = () => {
      btn.replaceChildren(...originalNodes);
      btn.disabled = false;
    };

    // Find the product form
    const formId = btn.getAttribute('data-form-id');
    const form = formId ? document.getElementById(formId) : btn.closest('form');

    if (!form) {
      console.error('[Buy it now] Product form not found. formId:', formId);
      return;
    }

    // Validate required fields (inside + outside the form)
    if (!this.validateForm(form)) return;

    // Show spinner
    btn.replaceChildren(...parseHTML(`<span class="flex items-center justify-center gap-2">${spinnerHtml} <span>Processing...</span></span>`));
    btn.disabled = true;

    try {
      // collectFormData handles ALL associated fields including outside-form elements
      const formData = this.collectFormData(form);

      let hasFile = false;
      for (const value of formData.values()) {
        if (value instanceof File && value.size > 0) {
          hasFile = true;
          break;
        }
      }

      // Save current cart and clear it for Buy It Now behavior
      try {
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        if (cart.items && cart.items.length > 0) {
          const itemsToSave = cart.items.map(item => ({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties
          }));
          localStorage.setItem('zuboc_saved_cart', JSON.stringify({ items: itemsToSave, timestamp: Date.now() }));
        }
        await fetch('/cart/clear.js', { method: 'POST' });
      } catch (e) {
        console.error('Error saving/clearing cart for Buy It Now', e);
      }

      let response;
      if (hasFile) {
        response = await fetch('/cart/add', {
          method: 'POST',
          body: formData
        });
      } else {
        response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
      }

      if (!response.ok) {
        let errMsg = 'Could not add to cart';
        try {
          errMsg = (await response.json()).description || errMsg;
        } catch (_) {}
        throw new Error(errMsg);
      }

      // Redirect to checkout using native form POST (most reliable method)
      this.redirectToCheckout();
    } catch (error) {
      console.error('[Buy it now] Error:', error);
      resetBtn();
    }
  }

  async addItem(variantId, quantity = 1) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.setLoading(true);

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });

      await this.evaluateFreeGift();
      await this.updateDrawer();
      await this.updateMainCart();
      this.open();
    } catch (error) {
      console.error('Error adding item:', error);
    } finally {
      this.isUpdating = false;
      this.setLoading(false);
    }
  }

  interceptAddToCart() {
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (
        form.getAttribute('action') === '/cart/add' ||
        form.getAttribute('action') === '/cart/add.js' ||
        (form.action && form.action.includes('/cart/add'))
      ) {
        e.preventDefault();

        if (this.isUpdating) return;
        this.isUpdating = true;
        this.setLoading(true);

        const spinnerHtml = `<svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`;
        const parseHTML = (html) => Array.from(new DOMParser().parseFromString(html, 'text/html').body.childNodes);

        const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
        let originalSubmitNodes = [];
        if (submitBtn) {
          originalSubmitNodes = Array.from(submitBtn.childNodes);
          const btnSpinner = submitBtn.querySelector('.btn-spinner');
          if (btnSpinner) {
            btnSpinner.classList.remove('hidden');
          } else {
            const btnText = submitBtn.innerText || submitBtn.value || 'Adding...';
            submitBtn.replaceChildren(...parseHTML(`<span class="flex items-center justify-center gap-2">${spinnerHtml} <span>${btnText}</span></span>`));
          }
          submitBtn.classList.add('opacity-70', 'pointer-events-none');
        }

        // Use collectFormData to include ALL associated fields (file uploads, text, etc.)
        const formData = this.collectFormData(form);

        // Strip empty properties[*] from formData to avoid sending blank properties to Shopify
        const cleanedFormData = new FormData();
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[') && key.endsWith(']') && (!value || value === '')) continue;
          cleanedFormData.append(key, value);
        }

        try {
          let hasFile = false;
          for (const value of cleanedFormData.values()) {
            if (value instanceof File && value.size > 0) {
              hasFile = true;
              break;
            }
          }

          let response;
          if (hasFile) {
            response = await fetch('/cart/add', {
              method: 'POST',
              body: cleanedFormData
            });
          } else {
            response = await fetch('/cart/add.js', {
              method: 'POST',
              body: cleanedFormData,
              headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
          }

          if (!response.ok) {
            let errMsg = 'Could not add to cart';
            try { errMsg = (await response.json()).description || errMsg; } catch (_) {}
            throw new Error(errMsg);
          }

          await this.evaluateFreeGift();
          await this.updateDrawer();
          await this.updateMainCart();
          this.open();
        } catch (error) {
          console.error('Error adding to cart:', error);
          // Show user-facing error
          if (submitBtn && submitBtn.isConnected) {
            submitBtn.replaceChildren(...originalSubmitNodes);
            submitBtn.classList.remove('opacity-70', 'pointer-events-none');
          }
          alert(error.message || 'Could not add item to cart. Please try again.');
        } finally {
          this.isUpdating = false;
          this.setLoading(false);

          if (submitBtn && submitBtn.isConnected) {
            submitBtn.replaceChildren(...originalSubmitNodes);
            submitBtn.classList.remove('opacity-70', 'pointer-events-none');
          }
        }
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});
